// @ts-check
/*
 Original code (c) 2017, Vladimir Agafonkin
 Simplify.js, a high-performance JS polyline simplification library
 mourner.github.io/simplify-js

 LICENSE
 Copyright (c) 2017, Vladimir Agafonkin
  All rights reserved.

  Redistribution and use in source and binary forms, with or without modification, are
  permitted provided that the following conditions are met:

    1. Redistributions of source code must retain the above copyright notice, this list of
        conditions and the following disclaimer.

    2. Redistributions in binary form must reproduce the above copyright notice, this list
        of conditions and the following disclaimer in the documentation and/or other materials
        provided with the distribution.

  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY
  EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF
  MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
  COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
  EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
  SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
  HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR
  TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
  SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

/**
 * Copied on 04/07/2024 from v1.2.4 of the simplify-js library.
 * This package was copied from the original source so that it could be modified to use the same point format as the
 * rest of the codebase.
 * Previously we were having to constantly map and convert between [x, y], {lat, lng} and { x, y },
 */

/**
 * @typedef {[number, number]} Point A point represented as an [x, y] array of two numbers.
 */

/**
 * square distance from a point to a segment
 * @param {Point} p the point to check
 * @param {Point} p1 the first point of the segment
 * @param {Point} p2 the second point of the segment
 */
function getSqSegDist(p, p1, p2) {
  let x = p1[0];
  let y = p1[1];
  let dx = p2[0] - x;
  let dy = p2[1] - y;

  if (dx !== 0 || dy !== 0) {
    const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);

    if (t > 1) {
      x = p2[0];
      y = p2[1];
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }

  dx = p[0] - x;
  dy = p[1] - y;

  return dx * dx + dy * dy;
}

/**
 * @param {Array<Point>} points
 * @param {number} first
 * @param {number} last
 * @param {number} sqTolerance
 * @param {Array<Point>} simplified
 */
function simplifyDPStep(points, first, last, sqTolerance, simplified) {
  let maxSqDist = sqTolerance;
  let index;
  for (let i = first + 1; i < last; i++) {
    const sqDist = getSqSegDist(points[i], points[first], points[last]);
    if (sqDist > maxSqDist) {
      index = i;
      maxSqDist = sqDist;
    }
  }
  if (maxSqDist > sqTolerance) {
    if (index - first > 1) {
      simplifyDPStep(points, first, index, sqTolerance, simplified);
    }
    simplified.push(points[index]);
    if (last - index > 1) {
      simplifyDPStep(points, index, last, sqTolerance, simplified);
    }
  }
}

/**
 * simplification using Ramer-Douglas-Peucker algorithm
 * @param {Array<Point>} points
 * @param {number} sqTolerance
 */
function simplifyDouglasPeucker(points, sqTolerance) {
  const last = points.length - 1;
  const simplified = [points[0]];
  simplifyDPStep(points, 0, last, sqTolerance, simplified);
  simplified.push(points[last]);
  return simplified;
}

/**
 * Simplifies an array of points based on a tolerance level. The larger the tolerance, the more points will be removed.
 *
 * The tolerance level is very sensitive to the precision of the input data. Points that have a small distance between
 * them will be removed very aggressively with even a small tolerance level. Points that have a very large distance
 * between them will not be removed until the tolerance level is very high.
 * @param {Array<Point>} points Array of points like `[[x, y], [x, y], ...]`
 * @param {number} tolerance Affects the amount of simplification (in the same metric as the point coordinates)
 */
function simplify(points, tolerance) {
  if (points.length <= 2) return points;
  const sqTolerance = tolerance !== undefined ? tolerance * tolerance : 1;
  points = simplifyDouglasPeucker(points, sqTolerance);
  return points;
}

export {
  simplify,
};
